import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Building, Home as HomeIcon, Hotel, Building2,
  Plus, Minus, Sparkles, Loader2, CheckCircle2, Ticket, X, Construction, Warehouse,
} from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PriceSummary from "@/components/booking/PriceSummary";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";
import { countryName, countryFlag } from "@/lib/countries";
import { US_STATES } from "@/lib/us-states";

const TOTAL_STEPS = 6;
const LS_KEY = "ec_booking_draft";

const PROPERTY_TYPES = [
  { code: "apartment", icon: Building },
  { code: "house", icon: HomeIcon },
  { code: "studio", icon: Hotel },
  { code: "condo", icon: Building2 },
  { code: "other", icon: Warehouse },
];

const RESIDENTIAL_MAX_SQFT = 10000;
const COMMERCIAL_MAX_SQFT = 100000;

const initialState = {
  step: 1,
  country: "US",
  zone_id: "",
  zone_state: "",
  selected_us_state: "",
  needs_coordination: false,
  property_type: "apartment",
  square_feet: 900,
  bedrooms: 2,
  bathrooms: 1,
  half_bathrooms: 0,
  tasks: [],
  space_description: "",
  full_name: "",
  email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  zip_code: "",
  state: "",
  additional_notes: "",
  preferred_date: "",
  preferred_time_slot: "flexible",
  consent: false,
  sms_consent: false,
  promo_code: "",
};

function loadDraft() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return initialState;
    return { ...initialState, ...JSON.parse(raw) };
  } catch { return initialState; }
}

function saveDraft(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

const stepVariants = {
  enter: { opacity: 0, x: 30 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
};

export default function BookingFlow() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState(loadDraft);
  const [zones, setZones] = useState([]);
  const [zonesLoaded, setZonesLoaded] = useState(false);
  const [rule, setRule] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [customQuoteRequired, setCustomQuoteRequired] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [promoInvalid, setPromoInvalid] = useState(false);
  const [promoNotApplicable, setPromoNotApplicable] = useState(null); // {min_subtotal} or null
  const [maintenance, setMaintenance] = useState(null); // {message} | null | undefined while loading
  const update = (patch) => setState((s) => ({ ...s, ...patch }));

  useEffect(() => { saveDraft(state); }, [state]);

  // Maintenance gate — check before fetching anything else
  useEffect(() => {
    api.get("/site-status").then((r) => {
      const d = unwrap(r) || {};
      if (d.maintenance_mode) {
        setMaintenance({ message: d.maintenance_message || "We're temporarily not accepting new bookings — back soon." });
      } else {
        setMaintenance(null);
      }
    }).catch(() => setMaintenance(null));
  }, []);

  // Active countries come from the dedicated endpoint (robust in production).
  // Fallback to {US} so the wizard is never stuck marking United States as "coming soon".
  const [activeCodes, setActiveCodes] = useState(new Set());
  useEffect(() => {
    api.get("/zones/countries")
      .then((r) => {
        const list = unwrap(r) || [];
        const codes = list.map((c) => c.code).filter(Boolean);
        setActiveCodes(new Set(codes.length ? codes : ["US"]));
      })
      .catch(() => setActiveCodes(new Set(["US"])));
    api.get("/pricing/active").then((r) => setRule(unwrap(r))).catch(() => {});
    api.get("/public/specialties").then((r) => setSpecialties(unwrap(r) || [])).catch(() => {});
  }, []);

  // The wizard operates in the US only for now. Country is hardcoded so a stale
  // localStorage draft (e.g. a previously picked country) can't break coverage.
  const selectedCountry = "US";
  // Covered = we have active zones for this country. Otherwise the wizard runs in
  // "coordination" mode: full booking, no immediate payment, admin coordinates a cleaner.
  const isCovered = activeCodes.has(selectedCountry) || (zonesLoaded && zones.length > 0);
  // Coordination mode = uncovered country OR the user picked "my state isn't listed yet"
  const coordinationMode = !isCovered || !!state.needs_coordination;
  /* eslint-disable */
  useEffect(() => {
    let cancelled = false;
    setZonesLoaded(false);
    const qsZone = searchParams.get("zone") || searchParams.get("zone_id");
    api.get(`/zones?country=${encodeURIComponent(selectedCountry)}`)
      .then((r) => {
        if (cancelled) return;
        const list = unwrap(r) || [];
        setZones(list);
        setZonesLoaded(true);
        setState((s) => {
          // Drop a stale zone_id left in a saved draft (zone ids change after a DB migration/import)
          const selected = s.zone_id ? list.find((z) => z.id === s.zone_id) : null;
          const zoneId = selected ? s.zone_id : "";
          const zoneState = zoneId ? s.zone_state : "";
          // Pre-select zone from ?zone= querystring (zone landing pages) when nothing is chosen yet
          if (!zoneId && qsZone) {
            const z = list.find((z) => z.id === qsZone);
            if (z) return { ...s, zone_id: z.id, zone_state: z.state, state: z.state };
          }
          return { ...s, zone_id: zoneId, zone_state: zoneState };
        });
      })
      .catch(() => { if (!cancelled) { setZones([]); setZonesLoaded(true); } });
    return () => { cancelled = true; };
  }, [selectedCountry]);
  /* eslint-enable */

  // Deep-link params from the home page ZIP quick-entry (and zone landing pages):
  //  • ?zone_id=<id>&step=2          → active zone, jump straight into the wizard
  //  • ?coordination=true&state&city&zip → uncovered ZIP, prefill the coordination flow
  /* eslint-disable */
  useEffect(() => {
    const patch = {};
    const zoneParam = searchParams.get("zone_id") || searchParams.get("zone");
    if (zoneParam) patch.needs_coordination = false; // the zones effect selects the zone
    if (searchParams.get("coordination") === "true") {
      patch.needs_coordination = true;
      patch.zone_id = "";
      patch.zone_state = "";
      const stParam = (searchParams.get("state") || "").trim();
      const cityParam = (searchParams.get("city") || "").trim();
      const zipParam = (searchParams.get("zip") || "").trim();
      if (stParam) {
        patch.state = US_STATES.find((s) => s.code === stParam.toUpperCase())?.name || stParam;
        patch.selected_us_state = stParam.length === 2 ? stParam.toUpperCase() : "";
      }
      if (cityParam) patch.city = cityParam;
      if (zipParam) patch.zip_code = zipParam;
    }
    const stepParam = parseInt(searchParams.get("step") || "", 10);
    if (Number.isInteger(stepParam) && stepParam >= 1 && stepParam <= TOTAL_STEPS) patch.step = stepParam;
    if (Object.keys(patch).length) setState((s) => ({ ...s, ...patch }));
  }, []);
  /* eslint-enable */

  const fetchQuote = useCallback(async () => {
    // Covered country with a chosen zone needs the zone; coordination quotes with no zone.
    const needsZone = isCovered && !state.needs_coordination;
    if (needsZone && !state.zone_id) { setBreakdown(null); return; }
    if (!isCovered && !zonesLoaded) { return; }
    setQuoteLoading(true);
    try {
      const r = await api.post("/pricing/quote", {
        zone_id: needsZone ? state.zone_id : null,
        property_type: state.property_type,
        square_feet: Number(state.square_feet),
        bedrooms: Number(state.bedrooms),
        bathrooms: Number(state.bathrooms),
        half_bathrooms: Number(state.half_bathrooms),
        selected_tasks: state.tasks,
        promo_code: state.promo_code || null,
      });
      const data = unwrap(r);
      setCustomQuoteRequired(!!data.commercial_custom_quote_required);
      setBreakdown(data.commercial_custom_quote_required ? null : data.breakdown);
      setPromoInvalid(!!data.promo_invalid);
      setPromoNotApplicable(data.promo_not_applicable ? { min_subtotal: data.promo_min_subtotal } : null);
    } catch { /* ignore */ }
    finally { setQuoteLoading(false); }
  }, [isCovered, zonesLoaded, state.needs_coordination, state.zone_id, state.property_type, state.square_feet, state.bedrooms, state.bathrooms, state.half_bathrooms, state.tasks, state.promo_code]);

  // Debounced quote fetch
  useEffect(() => {
    const id = setTimeout(fetchQuote, 300);
    return () => clearTimeout(id);
  }, [fetchQuote]);

  const canNext = useMemo(() => {
    switch (state.step) {
      case 1: return isCovered ? (!!state.zone_id || state.needs_coordination) : zonesLoaded;
      case 2: return !!state.property_type && (state.property_type !== "other" || !!state.space_description.trim());
      case 3: return state.square_feet >= 200;
      case 4: return state.bedrooms >= 0 && state.bathrooms >= 0;
      case 5: return true;
      case 6:
        return state.full_name && state.email && state.phone && state.address_line1 && state.city
          && (coordinationMode ? true : state.zip_code)
          && (state.needs_coordination ? state.state : true)
          && state.consent;
      default: return false;
    }
  }, [state, isCovered, coordinationMode, zonesLoaded]);

  const next = () => {
    if (!canNext) return;
    if (state.step < TOTAL_STEPS) update({ step: state.step + 1 });
    else submit();
  };
  const back = () => state.step > 1 && update({ step: state.step - 1 });

  const submit = async () => {
    if (!breakdown && !customQuoteRequired) { toast.error("Please wait for the quote to load"); return; }
    setSubmitting(true);
    try {
      const customer = {
        full_name: state.full_name,
        email: state.email,
        phone: state.phone,
        address_line1: state.address_line1,
        address_line2: state.address_line2 || null,
        city: state.city,
        state: state.state || state.zone_state || "",
        zip_code: state.zip_code || "",
        country: "US",
        sms_consent: state.sms_consent,
        sms_consent_text: state.sms_consent
          ? `${t("wizard.step6.sms_consent")} ${t("wizard.step6.privacy_policy_link")}.`
          : "",
      };
      const service = {
        zone_id: coordinationMode ? "" : state.zone_id,
        zone_name: coordinationMode ? "" : (zones.find((z) => z.id === state.zone_id)?.name || ""),
        city: state.city || "",
        country: "US",
        property_type: state.property_type,
        square_feet: Number(state.square_feet),
        bedrooms: Number(state.bedrooms),
        bathrooms: Number(state.bathrooms),
        half_bathrooms: Number(state.half_bathrooms),
        tasks: state.tasks,
        space_description: state.property_type === "other" ? state.space_description.trim() : "",
        additional_notes: state.additional_notes,
        preferred_date: state.preferred_date || null,
        preferred_time_slot: state.preferred_time_slot,
        locale,
      };
      try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
      if (!coordinationMode) {
        const r = await api.post("/bookings", { promo_code: state.promo_code || null, customer, service });
        const data = unwrap(r);
        // Backend may convert a no-zone booking into coordination — honor that
        navigate(data.requires_coordination ? `/coordination/${data.id}` : `/checkout/${data.id}`);
      } else {
        const r = await api.post("/bookings/coordination", { promo_code: state.promo_code || null, customer, service });
        const data = unwrap(r);
        navigate(`/coordination/${data.id}`);
      }
    } catch (err) {
      // Maintenance mode flipped to ON between page load and submit
      if (err?.response?.status === 503) {
        const msg = err?.response?.data?.detail?.message || "We're temporarily not taking new bookings.";
        setMaintenance({ message: msg });
        return;
      }
      toast.error(err?.response?.data?.detail?.message || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (state.step / TOTAL_STEPS) * 100;

  // Maintenance banner takes over the entire wizard area
  if (maintenance) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="maintenance-banner-page">
        <SEO title="Bookings paused — Ever Clean Home" description="We're temporarily not taking online bookings." noindex />
        <Header />
        <div className="flex-1 grid place-items-center px-4 md:px-8 py-16">
          <div className="max-w-xl w-full text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-amber-100 text-amber-700 grid place-items-center" data-testid="maintenance-banner-icon">
              <Construction className="w-10 h-10" />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mt-6" data-testid="maintenance-banner-title">
              We&apos;ll be right back.
            </h1>
            <p className="mt-4 text-lg text-[#4B5563]" data-testid="maintenance-banner-message">
              {maintenance.message}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" className="rounded-xl" onClick={() => navigate("/")} data-testid="maintenance-banner-home">
                Back to home
              </Button>
              <Button variant="outline" className="rounded-xl" onClick={() => navigate("/contact")} data-testid="maintenance-banner-contact">
                Contact us
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="booking-flow">
      <SEO
        title="Book your cleaning — Ever Clean Home"
        description="Get an instant quote and book residential cleaning in 60 seconds. Boston, Cambridge, Providence, Connecticut, Rhode Island."
      />
      <Header />

      <div className="max-w-5xl mx-auto px-4 md:px-8 w-full pt-8">
        <div className="flex items-center gap-3 text-sm text-[#4B5563] mb-3">
          <span className="font-mono uppercase tracking-wider text-xs text-[#0EA5A4] font-bold">
            {t("wizard.step")} {state.step} {t("wizard.of")} {TOTAL_STEPS}
          </span>
        </div>
        <Progress value={progress} className="h-2 bg-[#E5E7EB]" data-testid="wizard-progress" />
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 w-full grid grid-cols-1 lg:grid-cols-3 gap-8 py-8 lg:py-12 flex-1">
        <div className="lg:col-span-2 pb-32 lg:pb-0">
          <AnimatePresence mode="wait">
            <motion.div key={state.step} variants={stepVariants} initial="enter" animate="center" exit="exit"
              transition={{ duration: 0.25, ease: "easeOut" }}>
              {state.step === 1 && <Step1Zone zones={zones} zonesLoaded={zonesLoaded} activeCodes={activeCodes} isCovered={isCovered} state={state} update={update} t={t} locale={locale} />}
              {state.step === 2 && <Step2Property state={state} update={update} t={t} />}
              {state.step === 3 && <Step3Size state={state} update={update} t={t} customQuoteRequired={customQuoteRequired} />}
              {state.step === 4 && <Step4Rooms state={state} update={update} t={t} />}
              {state.step === 5 && <Step5Tasks rule={rule} specialties={specialties} state={state} update={update} t={t} locale={locale} />}
              {state.step === 6 && <Step6Contact state={state} update={update} t={t} zones={zones} coordinationMode={coordinationMode} breakdown={breakdown} promoInvalid={promoInvalid} promoNotApplicable={promoNotApplicable} />}
            </motion.div>
          </AnimatePresence>

          {/* Desktop nav buttons */}
          <div className="hidden lg:flex items-center justify-between mt-8">
            <Button variant="outline" onClick={back} disabled={state.step === 1} className="rounded-xl" data-testid="wizard-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t("wizard.back")}
            </Button>
            <Button onClick={next} disabled={!canNext || submitting}
              className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="wizard-next">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {state.step === TOTAL_STEPS ? ((coordinationMode || customQuoteRequired) ? t("wizard.finish_coordination") : t("wizard.finish")) : t("wizard.next")}
              {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>

        {/* Desktop price */}
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <PriceSummary breakdown={breakdown} loading={quoteLoading} hasZone={coordinationMode ? true : !!state.zone_id}
              propertyType={state.property_type} customQuoteRequired={customQuoteRequired} />
          </div>
        </div>
      </div>

      {/* Mobile bottom price + CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E7EB] shadow-top px-4 py-3" data-testid="mobile-price-bar">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#4B5563]">{t("wizard.summary.total")}</span>
          <span className="font-mono text-2xl font-bold text-[#0EA5A4]" data-testid="mobile-price-total">
            {customQuoteRequired ? t("wizard.summary.custom_quote_short") : `$${(breakdown?.total ?? 0).toFixed(2)}`}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={back} disabled={state.step === 1} className="rounded-xl px-3" data-testid="wizard-back-mobile">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Button onClick={next} disabled={!canNext || submitting} className="flex-1 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="wizard-next-mobile">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {state.step === TOTAL_STEPS ? ((coordinationMode || customQuoteRequired) ? t("wizard.finish_coordination") : t("wizard.finish")) : t("wizard.next")}
          </Button>
        </div>
      </div>

      <div className="mt-auto"><Footer /></div>
    </div>
  );
}

// ----- Step components ---------------------------------------------------------

function StepHeader({ title, subtitle }) {
  return (
    <div className="mb-7">
      <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">{title}</h2>
      <p className="mt-2 text-[#4B5563]">{subtitle}</p>
    </div>
  );
}

function Step1Zone({ zones, zonesLoaded, activeCodes, isCovered, state, update, t, locale }) {
  const country = "US";
  const countryZones = zones;

  return (
    <div data-testid="step-1">
      <StepHeader title={t("wizard.step1.title")} subtitle={t("wizard.step1.subtitle")} />

      {!zonesLoaded ? (
        <div className="grid place-items-center py-10" data-testid="step1-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
        </div>
      ) : isCovered ? (
        <div data-testid="zone-list">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {countryZones.map((z) => {
              const selected = state.zone_id === z.id && !state.needs_coordination;
              return (
                <button key={z.id}
                  onClick={() => update({ zone_id: z.id, zone_state: z.state, state: z.state, selected_us_state: z.state, needs_coordination: false })}
                  className={`text-left rounded-2xl p-5 border transition-all ${selected ? "border-[#0EA5A4] bg-[#0EA5A4]/5 ring-2 ring-[#0EA5A4]/30" : "border-[#E5E7EB] hover:border-[#0EA5A4]/50"}`}
                  data-testid={`zone-option-${z.slug || z.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      {z.state && <div className="font-mono text-xs uppercase tracking-wider text-[#0EA5A4] font-bold">{z.state}</div>}
                      <div className="mt-1 font-display text-lg font-bold">{z.name}</div>
                    </div>
                    {selected && <CheckCircle2 className="w-5 h-5 text-[#0EA5A4]" />}
                  </div>
                  <div className="mt-2 text-xs text-[#4B5563]">{(z.cities || []).slice(0, 4).join(" · ")}</div>
                </button>
              );
            })}
            {/* Escape hatch for covered NON-US countries (US uses the state dropdown below) */}
            {country !== "US" && (
              <button
                onClick={() => update({ needs_coordination: true, zone_id: "", zone_state: "", state: "", selected_us_state: "" })}
                className={`text-left rounded-2xl p-5 border border-dashed transition-all ${state.needs_coordination ? "border-[#0EA5A4] bg-[#0EA5A4]/5 ring-2 ring-[#0EA5A4]/30" : "border-[#0EA5A4]/40 hover:border-[#0EA5A4]"}`}
                data-testid="zone-option-other">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-xs uppercase tracking-wider text-[#0EA5A4] font-bold">{t("wizard.other_state.eyebrow")}</div>
                    <div className="mt-1 font-display text-lg font-bold">{t("wizard.other_state.title")}</div>
                  </div>
                  {state.needs_coordination && <CheckCircle2 className="w-5 h-5 text-[#0EA5A4]" />}
                </div>
                <div className="mt-2 text-xs text-[#4B5563]">{t("wizard.other_state.sub")}</div>
              </button>
            )}
          </div>
          {country === "US" && <UsStateSelect zones={countryZones} state={state} update={update} t={t} />}
        </div>
      ) : (
        <CoordinationNotice country={country} t={t} locale={locale} />
      )}
    </div>
  );
}

function UsStateSelect({ zones, state, update, t }) {
  const value = state.selected_us_state || state.zone_state || "";
  const onPick = (code) => {
    const matched = (zones || []).find((z) => z.state === code);
    if (matched) {
      update({ zone_id: matched.id, zone_state: matched.state, state: matched.state, selected_us_state: code, needs_coordination: false });
    } else {
      const name = US_STATES.find((s) => s.code === code)?.name || code;
      update({ needs_coordination: true, zone_id: "", zone_state: "", state: name, selected_us_state: code });
    }
  };
  return (
    <div className="mt-5">
      <Label className="text-sm">{t("wizard.step1.or_select_state")}</Label>
      <Select value={value} onValueChange={onPick}>
        <SelectTrigger className="rounded-xl mt-1 h-11" data-testid="us-state-select">
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {US_STATES.map((s) => {
            const covered = (zones || []).some((z) => z.state === s.code);
            return (
              <SelectItem key={s.code} value={s.code} textValue={s.name} data-testid={`us-state-${s.code}`}>
                <span className="flex items-center gap-2">
                  <span>{s.name}</span>
                  {covered && <span className="text-[10px] font-bold text-[#0EA5A4] uppercase">· active</span>}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

function CoordinationNotice({ country, t, locale }) {
  const cname = countryName(country, locale);
  return (
    <div className="rounded-2xl border border-[#0EA5A4]/30 bg-[#0EA5A4]/[0.05] p-6" data-testid="coordination-notice">
      <div className="flex items-start gap-4">
        <div className="text-4xl leading-none" aria-hidden>{countryFlag(country)}</div>
        <div>
          <div className="font-display text-2xl font-extrabold tracking-tighter" data-testid="coordination-notice-title">
            {t("wizard.coord.title", { country: cname })}
          </div>
          <p className="mt-2 text-[#4B5563] text-sm leading-relaxed">{t("wizard.coord.body")}</p>
          <ul className="mt-4 space-y-2 text-sm">
            {(t("wizard.coord.bullets") || []).map((b, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#0EA5A4] shrink-0 mt-0.5" />
                <span className="text-[#0C1A1A]">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Step2Property({ state, update, t }) {
  const onPick = (code) => {
    const patch = { property_type: code };
    if (code === "other") {
      patch.bedrooms = 0;
    } else if (state.square_feet > 20000) {
      patch.square_feet = 20000;
    }
    update(patch);
  };
  return (
    <div data-testid="step-2">
      <StepHeader title={t("wizard.step2.title")} subtitle={t("wizard.step2.subtitle")} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {PROPERTY_TYPES.map((p) => {
          const selected = state.property_type === p.code;
          const Icon = p.icon;
          return (
            <button key={p.code} onClick={() => onPick(p.code)}
              className={`rounded-2xl p-5 border transition-all flex flex-col items-center text-center ${selected ? "border-[#0EA5A4] bg-[#0EA5A4]/5 ring-2 ring-[#0EA5A4]/30" : "border-[#E5E7EB] hover:border-[#0EA5A4]/50"}`}
              data-testid={`property-${p.code}`}>
              <span className={`w-12 h-12 rounded-xl grid place-items-center mb-3 ${selected ? "bg-[#0EA5A4] text-white" : "bg-[#F5F5F4] text-[#0C1A1A]"}`}>
                <Icon className="w-5 h-5" />
              </span>
              <span className="font-semibold text-sm">{t(`wizard.properties.${p.code}`)}</span>
              {p.code === "other" && <span className="mt-1 text-xs text-[#4B5563] leading-snug">{t("wizard.properties.other_sub")}</span>}
            </button>
          );
        })}
      </div>
      {state.property_type === "other" && (
        <div className="mt-5">
          <Label className="text-sm">{t("wizard.step2.space_description_label")} *</Label>
          <Input value={state.space_description} onChange={(e) => update({ space_description: e.target.value })}
            placeholder={t("wizard.step2.space_description_placeholder")}
            className="rounded-xl mt-1" data-testid="input-space-description" />
        </div>
      )}
    </div>
  );
}

function Step3Size({ state, update, t, customQuoteRequired }) {
  const isOther = state.property_type === "other";
  const maxSqft = isOther ? COMMERCIAL_MAX_SQFT : RESIDENTIAL_MAX_SQFT;
  return (
    <div data-testid="step-3">
      <StepHeader title={t("wizard.step3.title")} subtitle={t("wizard.step3.subtitle")} />
      <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
        <div className="text-center mb-6">
          <div className="font-mono text-5xl font-bold text-[#0EA5A4]" data-testid="sqft-value">
            {state.square_feet.toLocaleString()}
          </div>
          <div className="text-sm text-[#4B5563] mt-1">{t("wizard.step3.unit")}</div>
        </div>
        <Slider value={[state.square_feet]} min={200} max={maxSqft} step={isOther ? 100 : 50}
          onValueChange={(v) => update({ square_feet: v[0] })}
          className="mt-2"
          data-testid="sqft-slider" />
        <div className="flex justify-between text-xs text-[#9CA3AF] font-mono mt-2">
          <span>200</span><span>{Math.round(maxSqft / 2).toLocaleString()}</span><span>{maxSqft.toLocaleString()}</span>
        </div>
        <div className="mt-6">
          <Input type="number" min={200} max={maxSqft} value={state.square_feet}
            onChange={(e) => update({ square_feet: Math.max(200, Math.min(maxSqft, Number(e.target.value) || 0)) })}
            className="rounded-xl text-center font-mono"
            data-testid="sqft-input" />
        </div>
      </Card>
      {isOther && customQuoteRequired && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="commercial-custom-quote-banner">
          {t("wizard.custom_quote.message")}
        </div>
      )}
    </div>
  );
}

function Counter({ label, value, onChange, min = 0, max = 20, testid }) {
  return (
    <Card className="rounded-2xl p-5 border border-[#E5E7EB]">
      <div className="flex items-center justify-between">
        <span className="font-semibold">{label}</span>
        <div className="flex items-center gap-3">
          <button onClick={() => onChange(Math.max(min, value - 1))}
            className="w-10 h-10 rounded-xl border border-[#E5E7EB] grid place-items-center hover:border-[#0EA5A4] disabled:opacity-30"
            disabled={value <= min} data-testid={`${testid}-minus`}>
            <Minus className="w-4 h-4" />
          </button>
          <span className="font-mono text-2xl font-bold w-8 text-center" data-testid={`${testid}-value`}>{value}</span>
          <button onClick={() => onChange(Math.min(max, value + 1))}
            className="w-10 h-10 rounded-xl border border-[#E5E7EB] grid place-items-center hover:border-[#0EA5A4]"
            data-testid={`${testid}-plus`}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Card>
  );
}

function Step4Rooms({ state, update, t }) {
  const isOther = state.property_type === "other";
  return (
    <div data-testid="step-4">
      <StepHeader title={isOther ? t("wizard.step4.title_other") : t("wizard.step4.title")} subtitle={t("wizard.step4.subtitle")} />
      <div className="space-y-3">
        {!isOther && <Counter label={t("wizard.step4.bedrooms")} value={state.bedrooms} onChange={(v) => update({ bedrooms: v })} max={10} testid="bedrooms" />}
        <Counter label={t("wizard.step4.bathrooms")} value={state.bathrooms} onChange={(v) => update({ bathrooms: v })} max={10} testid="bathrooms" />
        <Counter label={t("wizard.step4.half_bathrooms")} value={state.half_bathrooms} onChange={(v) => update({ half_bathrooms: v })} max={10} testid="half-bathrooms" />
      </div>
    </div>
  );
}

function Step5Tasks({ rule, specialties, state, update, t, locale }) {
  // Catalog is the source of truth for which tasks are offered. Prices come
  // from the active pricing rule (optional overlay). If a catalog entry has
  // no matching priced task, render it without a price chip.
  const priceMap = useMemo(() => {
    const m = new Map();
    (rule?.tasks || []).forEach((tk) => {
      if (tk.is_active !== false) m.set(tk.code, tk.price);
    });
    return m;
  }, [rule]);
  const catalogList = (specialties && specialties.length > 0)
    ? specialties.map((s) => ({ code: s.slug, label_en: s.label_en, label_es: s.label_es, price: priceMap.get(s.slug) }))
    : (rule?.tasks || []).filter((tt) => tt.is_active !== false);
  const toggle = (code) => {
    update({ tasks: state.tasks.includes(code) ? state.tasks.filter((c) => c !== code) : [...state.tasks, code] });
  };
  return (
    <div data-testid="step-5">
      <StepHeader title={t("wizard.step5.title")} subtitle={t("wizard.step5.subtitle")} />
      <div className="rounded-2xl bg-[#0EA5A4]/5 border border-[#0EA5A4]/20 p-5 mb-5" data-testid="whats-included-box">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-4 h-4 text-[#0EA5A4] shrink-0" />
          <span className="font-display font-bold text-sm">{t("wizard.included.title")}</span>
        </div>
        <ul className="space-y-1 pl-1">
          {t("wizard.included.items").map((item, i) => (
            <li key={i} className="flex gap-2 text-sm text-[#4B5563]">
              <span className="text-[#0EA5A4]">•</span><span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-[#4B5563] mt-3 italic">{t("wizard.included.closing")}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {catalogList.map((tk) => {
          const selected = state.tasks.includes(tk.code);
          const label = locale === "es" ? tk.label_es : tk.label_en;
          const hasPrice = typeof tk.price === "number" && tk.price > 0;
          return (
            <button key={tk.code} onClick={() => toggle(tk.code)}
              className={`text-left rounded-2xl p-4 border transition-all flex items-center justify-between ${selected ? "border-[#0EA5A4] bg-[#0EA5A4]/5 ring-2 ring-[#0EA5A4]/30" : "border-[#E5E7EB] hover:border-[#0EA5A4]/50"}`}
              data-testid={`task-${tk.code}`}>
              <div className="flex items-center gap-3">
                <span className={`w-5 h-5 rounded-md border-2 grid place-items-center transition-colors ${selected ? "bg-[#0EA5A4] border-[#0EA5A4]" : "border-[#9CA3AF]"}`}>
                  {selected && <CheckCircle2 className="w-4 h-4 text-white" />}
                </span>
                <span className="font-medium text-sm">{label}</span>
              </div>
              {hasPrice && (
                <span className="font-mono font-semibold text-sm text-[#0EA5A4]">+${tk.price.toFixed(2)}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Step6Contact({ state, update, t, breakdown, promoInvalid, promoNotApplicable, zones, coordinationMode }) {
  const [zipCheck, setZipCheck] = useState({ status: "idle", zoneName: "" });

  // Validate the entered ZIP locally against the zone the customer ALREADY picked
  // in Step 1 (each zone carries its full zip_codes list). Skipped entirely for
  // coordination (uncovered) bookings where ZIP is optional and there's no zone.
  /* eslint-disable */
  useEffect(() => {
    if (coordinationMode) { setZipCheck({ status: "idle", zoneName: "" }); return; }
    const zip = (state.zip_code || "").trim();
    if (zip.length < 5) { setZipCheck({ status: "idle", zoneName: "" }); return; }
    const list = zones || [];
    const inZone = (z) => (z.zip_codes || []).includes(zip);
    const inZonePrefix = (z) => zip.length >= 3 && (z.zip_codes || []).some((c) => String(c).startsWith(zip.slice(0, 3)));

    const selected = list.find((z) => z.id === state.zone_id);
    // 1) ZIP explicitly listed in the selected zone → all good, no warning
    if (selected && inZone(selected)) { setZipCheck({ status: "match", zoneName: selected.name }); return; }
    // 2) ZIP explicitly listed in another zone → suggest going back to pick it
    const other = list.find((z) => inZone(z));
    if (other) { setZipCheck({ status: "mismatch", zoneName: other.name }); return; }
    // 3) No exact hit anywhere — fall back to a 3-digit prefix, selected zone first
    if (selected && inZonePrefix(selected)) { setZipCheck({ status: "match", zoneName: selected.name }); return; }
    const otherPrefix = list.find((z) => inZonePrefix(z));
    if (otherPrefix) { setZipCheck({ status: "mismatch", zoneName: otherPrefix.name }); return; }
    // 4) Not covered by any service area yet
    setZipCheck({ status: "outside", zoneName: "" });
  }, [state.zip_code, state.zone_id, zones, coordinationMode]);
  /* eslint-enable */

  const zipBanner = (() => {
    if (zipCheck.status === "outside")
      return <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="zip-warning-outside">{t("wizard.zip.outside")}</div>;
    if (zipCheck.status === "mismatch")
      return <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="zip-warning-mismatch">{t("wizard.zip.mismatch", { zone: zipCheck.zoneName })}</div>;
    if (zipCheck.status === "match")
      return <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800" data-testid="zip-ok">{t("wizard.zip.match", { zone: zipCheck.zoneName })}</div>;
    return null;
  })();

  return (
    <div data-testid="step-6">
      <StepHeader title={t("wizard.step6.title")} subtitle={t("wizard.step6.subtitle")} />
      <Card className="rounded-2xl p-5 border border-[#E5E7EB] space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">{t("wizard.step6.full_name")} *</Label>
            <Input value={state.full_name} onChange={(e) => update({ full_name: e.target.value })}
              className="rounded-xl mt-1" placeholder="Jane Doe" data-testid="input-full-name" />
          </div>
          <div>
            <Label className="text-sm">{t("wizard.step6.email")} *</Label>
            <Input type="email" value={state.email} onChange={(e) => update({ email: e.target.value })}
              className="rounded-xl mt-1" placeholder="you@email.com" data-testid="input-email" />
          </div>
          <div>
            <Label className="text-sm">{t("wizard.step6.phone")} *</Label>
            <Input value={state.phone} onChange={(e) => update({ phone: e.target.value })}
              className="rounded-xl mt-1" placeholder="(555) 123-4567" data-testid="input-phone" />
          </div>
          <div>
            <Label className="text-sm">{t("wizard.step6.zip_code")}{!coordinationMode ? " *" : ""}</Label>
            <Input value={state.zip_code} onChange={(e) => update({ zip_code: e.target.value })}
              className="rounded-xl mt-1 font-mono" placeholder="02108" data-testid="input-zip" />
            {zipBanner && <div className="mt-2">{zipBanner}</div>}
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm">{t("wizard.step6.address_line1")} *</Label>
            <Input value={state.address_line1} onChange={(e) => update({ address_line1: e.target.value })}
              className="rounded-xl mt-1" placeholder="123 Main St" data-testid="input-address1" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm">{t("wizard.step6.address_line2")}</Label>
            <Input value={state.address_line2} onChange={(e) => update({ address_line2: e.target.value })}
              className="rounded-xl mt-1" placeholder={t("wizard.step6.address_line2_placeholder")} data-testid="input-address2" />
          </div>
          <div className={coordinationMode ? "" : "sm:col-span-2"}>
            <Label className="text-sm">{t("wizard.step6.city")} *</Label>
            <Input value={state.city} onChange={(e) => update({ city: e.target.value })}
              className="rounded-xl mt-1" placeholder="Boston" data-testid="input-city" />
          </div>
          {coordinationMode && (
            <div>
              <Label className="text-sm">{t("wizard.step6.state")}{state.needs_coordination ? " *" : ""}</Label>
              <Input value={state.state} onChange={(e) => update({ state: e.target.value })}
                className="rounded-xl mt-1" placeholder={t("wizard.step6.state_placeholder")} data-testid="input-state" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">{t("wizard.step6.date_label")}</Label>
            <Input type="date" value={state.preferred_date} onChange={(e) => update({ preferred_date: e.target.value })}
              className="rounded-xl mt-1" data-testid="input-date" />
          </div>
          <div>
            <Label className="text-sm">{t("wizard.step6.time_label")}</Label>
            <select value={state.preferred_time_slot}
              onChange={(e) => update({ preferred_time_slot: e.target.value })}
              className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
              data-testid="input-time">
              <option value="morning">{t("wizard.step6.time_morning")}</option>
              <option value="midday">{t("wizard.step6.time_midday")}</option>
              <option value="afternoon">{t("wizard.step6.time_afternoon")}</option>
              <option value="evening">{t("wizard.step6.time_evening")}</option>
              <option value="night">{t("wizard.step6.time_night")}</option>
              <option value="flexible">{t("wizard.step6.time_flexible")}</option>
            </select>
          </div>
        </div>

        <div>
          <Label className="text-sm">{t("wizard.step6.notes_label")}</Label>
          <Textarea rows={6} value={state.additional_notes} onChange={(e) => update({ additional_notes: e.target.value })}
            placeholder={t("wizard.step6.notes_placeholder")} className="rounded-xl mt-1" data-testid="input-notes" />
        </div>

        <PromoInput state={state} update={update} promoInvalid={promoInvalid} promoNotApplicable={promoNotApplicable} breakdown={breakdown} t={t} />

        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-checkbox-label">
          <Checkbox checked={state.consent} onCheckedChange={(v) => update({ consent: !!v })} className="mt-0.5" data-testid="consent-checkbox" />
          <span className="text-sm text-[#4B5563]">{t("wizard.step6.consent")}</span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3.5" data-testid="sms-consent-checkbox-label">
          <Checkbox checked={state.sms_consent} onCheckedChange={(v) => update({ sms_consent: !!v })} className="mt-0.5" data-testid="sms-consent-checkbox" />
          <span className="text-sm text-[#4B5563]">
            {t("wizard.step6.sms_consent")}{" "}
            <Link to="/privacy#sms" target="_blank" rel="noopener" className="underline hover:text-[#0EA5A4]" data-testid="sms-consent-privacy-link">
              {t("wizard.step6.privacy_policy_link")}
            </Link>.
          </span>
        </label>
      </Card>
    </div>
  );
}

function PromoInput({ state, update, promoInvalid, promoNotApplicable, breakdown, t }) {
  const [open, setOpen] = useState(!!state.promo_code);
  const applied = !!(state.promo_code && breakdown?.discount > 0);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="text-sm font-semibold text-[#0EA5A4] hover:underline flex items-center gap-1.5"
        data-testid="promo-toggle">
        <Ticket className="w-4 h-4" /> {t("wizard.promo.have_code")}
      </button>
    );
  }
  return (
    <div className="space-y-2" data-testid="promo-block">
      <Label className="text-sm flex items-center gap-1.5">
        <Ticket className="w-3.5 h-3.5" /> {t("wizard.promo.label")}
      </Label>
      <div className="flex gap-2">
        <Input value={state.promo_code}
          onChange={(e) => update({ promo_code: e.target.value.toUpperCase().replace(/\s+/g, "") })}
          placeholder="SUMMER10"
          className="rounded-xl font-mono uppercase tracking-wider"
          data-testid="promo-input" />
        {state.promo_code && (
          <button type="button" onClick={() => { update({ promo_code: "" }); setOpen(false); }}
            className="w-10 h-10 rounded-xl border border-[#E5E7EB] grid place-items-center hover:border-red-300 hover:text-red-500"
            data-testid="promo-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {applied && (
        <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1.5" data-testid="promo-applied">
          <CheckCircle2 className="w-3.5 h-3.5" /> {t("wizard.promo.applied")} — ${breakdown.discount.toFixed(2)} {t("wizard.promo.off")}
        </div>
      )}
      {promoNotApplicable?.min_subtotal && state.promo_code && !applied && (
        <div className="text-xs text-amber-700" data-testid="promo-min-subtotal">
          {t("wizard.promo.min_subtotal", { amount: `$${Number(promoNotApplicable.min_subtotal).toFixed(2)}` })}
        </div>
      )}
      {promoInvalid && state.promo_code && !applied && (
        <div className="text-xs text-rose-600" data-testid="promo-invalid">{t("wizard.promo.invalid")}</div>
      )}
    </div>
  );
}
